import { createSelector } from '@reduxjs/toolkit';
import { useMutation } from '@tanstack/react-query';
import clsx from 'clsx';
import { CheckBox } from 'grommet';
import { ComponentProps, useState } from 'preact/compat';
import { useEffect } from 'preact/hooks';
import { useDispatch } from 'react-redux';

import { sortHabitats } from '@/helpers/habitatsSorter';
import { HabitatListItem } from '@/queries/habitats/types';
import { patchUserMutationFn, postUserAppOnboardedMutationFn, postUserMarketingOptInMutationFn } from '@/queries/users';
import { AppState, useStore } from '@/redux/helper';
import { updateUserProperty } from '@/redux/slices/user/actions';
import { trackMP } from '@/shared/mp';

import { flagAppOnboardingModalStep } from './redux/actions';

const selectHabitats = createSelector(
  (state: AppState) => state.allHabitats,
  (habitats) => sortHabitats(habitats, false, ['online', 'currentViewers', 'index', 'id'], 0).slice(0, 10),
);

const AppOnboardingFavoriteAnimals = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const [isMarketingOptIn, setIsMarketingOptIn] = useState(false);
  const [selectedHabitats, setSelectedHabitats] = useState<Set<string>>(() => new Set());
  const [habitats, setHabitats] = useState<HabitatListItem[]>(() => selectHabitats(store.getState()));

  const { mutate: updateMarketingOptIn } = useMutation({
    mutationFn: postUserMarketingOptInMutationFn,
    onSuccess: (data) => {
      if (data?.user) {
        dispatch(updateUserProperty({ isMarketingOptIn: data.user.isMarketingOptIn }));
      }
    },
  });

  const { mutate: markOnboarded } = useMutation({
    mutationFn: postUserAppOnboardedMutationFn,
    onSuccess: (data) => {
      dispatch(updateUserProperty(data));
    },
  });

  const { mutate: patchUserFavoriteHabitats } = useMutation({
    mutationFn: patchUserMutationFn,
    onSuccess: (data) => {
      if (data?.favoriteHabitats) {
        dispatch(updateUserProperty({ favoriteHabitats: data.favoriteHabitats }));
      }
    },
  });

  const handleHabitatClick = (habitatId: string) => {
    setSelectedHabitats((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(habitatId)) {
        newSet.delete(habitatId);
      } else {
        newSet.add(habitatId);
      }
      return newSet;
    });
  };

  const finishOnboarding = (skipSaving: boolean) => {
    if (!skipSaving) {
      updateMarketingOptIn({ isMarketingOptIn });
      patchUserFavoriteHabitats({ favoriteHabitats: Array.from(selectedHabitats) });
    }
    markOnboarded();
    dispatch(flagAppOnboardingModalStep('favoriteAnimals'));
    trackMP('after-signup-step', { step: 'favoriteAnimals', chosenUpdatesOptIn: isMarketingOptIn, skipSaving });
  };

  const handleSubmit: ComponentProps<'form'>['onSubmit'] = (e) => {
    e.preventDefault();
    finishOnboarding((e as any).submitter?.value === 'Not Right Now');
  };

  // only update the cached habitats if there are no habitats in the cache
  useEffect(() => {
    if (habitats.length > 0) {
      return undefined;
    }
    const unsubscribe = store.subscribe(() => {
      setHabitats(selectHabitats(store.getState()));
    });
    return () => unsubscribe();
  }, [store, habitats]);

  return (
    <form onSubmit={handleSubmit} className="flex h-full flex-col">
      <div className="shrink-0">
        <h1 className="preflight preflight-h1 p-5 pt-10 text-center text-2xl/7 font-bold text-[#162B1B]">
          Do you have any favorite animals?
        </h1>
      </div>
      <div className="min-h-0 grow overflow-y-auto px-4">
        <ul className="preflight preflight-ul flex flex-wrap gap-2">
          {habitats.map((habitat) => {
            const isSelected = selectedHabitats.has(habitat._id);
            return (
              <li key={habitat._id}>
                <button
                  type="button"
                  role="checkbox"
                  onClick={() => handleHabitatClick(habitat._id)}
                  aria-checked={isSelected}
                  className={clsx(
                    'preflight preflight-button flex cursor-pointer items-center rounded-lg border p-2 transition-colors hover:border-primary-green',
                    isSelected ? 'border-primary-green bg-[#F0F2E1]' : 'border-gray-300 bg-white',
                  )}
                >
                  <img
                    src={habitat.profileImage}
                    alt={habitat.title}
                    className="preflight preflight-img mr-2 size-6 rounded-md"
                  />
                  <span className="text-sm/6 font-medium text-stone-700">{habitat.title}</span>
                </button>
              </li>
            );
          })}
        </ul>
        <p className="preflight preflight-p my-2 text-center text-base/6 font-medium text-primary-green">+100s more!</p>
      </div>
      <div className="flex shrink-0 flex-col justify-center gap-3 px-10 py-5">
        <CheckBox
          checked={isMarketingOptIn}
          label={
            <div className="flex items-center justify-center">
              <span className="text-sm text-primary-green">Sign me up to zoolife updates</span>
            </div>
          }
          onChange={() => setIsMarketingOptIn(!isMarketingOptIn)}
        />

        <button
          type="submit"
          className="preflight preflight-button w-full rounded-full bg-primary-green p-2 text-sm/6 font-medium text-white hover:bg-secondary-green"
        >
          Continue where you left off
        </button>

        <input
          type="submit"
          value="Not Right Now"
          className="preflight preflight-input preflight-button text-xs text-gray-500 underline underline-offset-2"
        />
      </div>
    </form>
  );
};

export default AppOnboardingFavoriteAnimals;
